import { complementarColors, institutionalColors, secondaryTones } from '~/design'

export type AnalyticalLibraryTheme = {
  name: string
  color: string
}

export type AnalyticalLibraryItem = {
  title: string
  themeSlug: string
  productType: string
  imageSource?: string
  imageAlternativeText: string
  description: string
  technology: string
  navigationType: string
  destinationPath: string
  redirect?: boolean
  isEmbedded?: boolean
  realLink?: string
  multipleLinks?: { title: string; destinationPath: string }[]
}

export const analyticalLibraryThemes: Record<string, AnalyticalLibraryTheme> = {
  'solutions-portfolio': { name: 'Portfólio de Soluções', color: complementarColors.pomegranate },
  'market-intelligence': { name: 'Inteligência de Mercado', color: complementarColors.nephritis },
  'sebrae-agents-at-your-company': {
    name: 'Agentes SEBRAE na Sua Empresa',
    color: institutionalColors.blueSebrae36,
  },
  'internal-monitoring': { name: 'Monitoramentos Internos', color: secondaryTones[2] },
  others: { name: 'Outros', color: complementarColors.deviantPurple },
}

const analyticalLibraryItems: AnalyticalLibraryItem[] = [
  {
    title: 'Relatório Padrão de Monitoramento de Portfólios',
    themeSlug: 'solutions-portfolio',
    productType: 'Relatório',
    imageSource:
      'https://images.unsplash.com/photo-1711097383282-28097ae16b1d?auto=format&fit=crop&fm=jpg&q=80&w=1200',
    imageAlternativeText: 'Painel de dados',
    description:
      'Visão executiva dos principais resultados dos portfólios de soluções e de seus indicadores.',
    technology: 'Nativo',
    navigationType: 'Nativo',
    destinationPath: '/app/report/standard-portfolio-report',
  },
  {
    title: 'Resumo Executivo',
    themeSlug: 'solutions-portfolio',
    productType: 'Painel',
    imageSource: '/report_assets/indicator_trends.png',
    imageAlternativeText: 'Resumo executivo',
    description: 'Acompanhe a direção dos indicadores no período mais recente.',
    technology: 'Nativo',
    navigationType: 'Nativo',
    destinationPath: '/app/report/indicator-trends',
  },
  {
    title: 'Atendimentos na Visão do Funil de Oferta',
    themeSlug: 'solutions-portfolio',
    productType: 'Painel',
    imageSource: '/report_assets/service_interactions_by_offer_funnel.png',
    imageAlternativeText: 'Funil de oferta',
    description:
      'Analisa os atendimentos por macroetapa, microetapa, tipo de atendimento e produto.',
    technology: 'Power BI',
    navigationType: 'Iframe',
    destinationPath: '/app/external-report/atendimentos-na-visao-do-funil-de-oferta',
    isEmbedded: true,
    realLink:
      'https://app.powerbi.com/reportEmbed?reportId=8e3c2f69-3a52-45f8-bb9d-c2ca548d32fe&autoAuth=true&ctid=97298271-1bd7-4ac5-935b-88addef636cc',
  },
  {
    title: 'BI do Portfólio Gestão Empresarial',
    themeSlug: 'solutions-portfolio',
    productType: 'Painel',
    imageSource: '/report_assets/business_management_portfolio.png',
    imageAlternativeText: 'Painel de gestão empresarial',
    description:
      'Acompanha saúde, utilização, interesse, públicos e qualidade cadastral do portfólio.',
    technology: 'Power BI',
    navigationType: 'Iframe',
    destinationPath: '/app/external-report/bi-portfolio-gestao-empresarial',
    isEmbedded: true,
    multipleLinks: [
      {
        title: 'Visão Geral',
        destinationPath:
          'https://app.powerbi.com/reportEmbed?reportId=ccd089c2-55e2-4b36-9347-edb3c520a977&appId=755f0f31-2eba-45f8-b415-b2afc0ada877&autoAuth=true&ctid=97298271-1bd7-4ac5-935b-88addef636cc',
      },
      {
        title: 'Visão por Produto',
        destinationPath:
          'https://app.powerbi.com/reportEmbed?reportId=e3ac1d3e-2e27-4cf6-afd8-bb93676af504&appId=755f0f31-2eba-45f8-b415-b2afc0ada877&autoAuth=true&ctid=97298271-1bd7-4ac5-935b-88addef636cc',
      },
      {
        title: 'Análise de Duplicidades',
        destinationPath:
          'https://app.powerbi.com/reportEmbed?reportId=f31c02bd-a892-450d-9aed-7a615af4aafe&appId=755f0f31-2eba-45f8-b415-b2afc0ada877&autoAuth=true&ctid=97298271-1bd7-4ac5-935b-88addef636cc',
      },
    ],
  },
  {
    title: 'BI do Portfólio Educação Empreendedora',
    themeSlug: 'solutions-portfolio',
    productType: 'Painel',
    imageSource: '/report_assets/entrepreneurial_education_portfolio.png',
    imageAlternativeText: 'Painel de educação empreendedora',
    description:
      'Acompanha saúde, utilização, interesse, públicos e qualidade cadastral do portfólio.',
    technology: 'Power BI',
    navigationType: 'Iframe',
    destinationPath: '/app/external-report/bi-portfolio-educacao-empreendedora',
    isEmbedded: true,
    multipleLinks: [
      {
        title: 'Visão Geral',
        destinationPath:
          'https://app.powerbi.com/reportEmbed?reportId=9a722541-1461-4452-82d3-cf865ab3ab92&appId=755f0f31-2eba-45f8-b415-b2afc0ada877&autoAuth=true&ctid=97298271-1bd7-4ac5-935b-88addef636cc',
      },
      {
        title: 'Visão por Produto',
        destinationPath:
          'https://app.powerbi.com/reportEmbed?reportId=52003c0c-ff1f-4202-a745-be6ed37cc6d4&appId=755f0f31-2eba-45f8-b415-b2afc0ada877&autoAuth=true&ctid=97298271-1bd7-4ac5-935b-88addef636cc',
      },
    ],
  },
  {
    title: 'Painel NPS de Soluções',
    themeSlug: 'solutions-portfolio',
    productType: 'Painel',
    imageSource: '/report_assets/nps_solutions.png',
    imageAlternativeText: 'Painel NPS de soluções',
    description: 'Acompanha o NPS por modalidade, solução e UF, além de comentários abertos.',
    technology: 'Power BI',
    navigationType: 'Iframe',
    destinationPath: '/app/external-report/painel-nps-de-solucoes',
    isEmbedded: true,
    realLink:
      'https://app.powerbi.com/reportEmbed?reportId=c88ec055-5c03-40ad-a60f-df50a83dde40&autoAuth=true&ctid=97298271-1bd7-4ac5-935b-88addef636cc',
  },
  {
    title: 'KPI de Monitoramento de Soluções',
    themeSlug: 'solutions-portfolio',
    productType: 'Painel',
    imageSource: '/report_assets/solutions_monitoring_kpi.png',
    imageAlternativeText: 'Painel de indicadores de monitoramento de soluções',
    description:
      'Consolida KPIs da Unidade de Gestão de Soluções por produto, período e território.',
    technology: 'Qlik Sense',
    navigationType: 'Redirect',
    destinationPath:
      'https://analisededados.sebrae.com.br/sense/app/8c048b8f-69e9-44ca-b0f7-f42f5e0797d5',
    redirect: true,
  },
  {
    title: 'Buscar Empresas',
    themeSlug: 'market-intelligence',
    productType: 'Ferramenta',
    imageSource: '/report_assets/company_search.png',
    imageAlternativeText: 'Busca de empresas',
    description:
      'Permite pesquisar empresas e consultar informações cadastrais, de contato e localização.',
    technology: 'Nativo',
    navigationType: 'Nativo',
    destinationPath: '/app/searchLegalEntity',
  },
  {
    title: 'Mapa de Demarcação',
    themeSlug: 'market-intelligence',
    productType: 'Mapa',
    imageSource: '/report_assets/demarcation_map.png',
    imageAlternativeText: 'Mapa com demarcação territorial',
    description:
      'Permite delimitar uma área por raio ou polígono e consultar indicadores territoriais e empresariais.',
    technology: 'Nativo',
    navigationType: 'Nativo',
    destinationPath: '/app/map/demarcation',
  },
  {
    title: 'Atendimento Desproporcional',
    themeSlug: 'market-intelligence',
    productType: 'Estudo',
    imageSource: '/report_assets/disproportionate_attention.png',
    imageAlternativeText: 'Marcação de localização',
    description:
      'Analisa concentrações de atendimentos e identifica territórios com comportamento desproporcional.',
    technology: 'Nativo',
    navigationType: 'Nativo',
    destinationPath: '/app/disproportionate-attention',
  },
  {
    title: 'Marketshare do Emissor NF-e SEBRAE',
    themeSlug: 'market-intelligence',
    productType: 'Painel',
    imageSource: '/report_assets/marketshare_emissor_nfe_sebrae.png',
    imageAlternativeText: 'Painel de participação do Emissor NF-e SEBRAE',
    description:
      'Mede a participação do Emissor NF-e no universo de empresas ativas por UF, porte e atividade econômica.',
    technology: 'Power BI',
    navigationType: 'Iframe',
    destinationPath: '/app/external-report/marketshare-do-emissor-nfe-sebrae',
    isEmbedded: true,
    realLink:
      'https://app.powerbi.com/reportEmbed?reportId=4c400b56-31a8-4ef4-a75e-eacf4071e0fa&autoAuth=true&ctid=97298271-1bd7-4ac5-935b-88addef636cc',
  },
  {
    title: 'Variação do Faturamento Mensal dos Pequenos Negócios',
    themeSlug: 'market-intelligence',
    productType: 'Aplicação analítica',
    imageSource: '/report_assets/monthly_revenue_average.png',
    imageAlternativeText: 'Gráfico de variação de faturamento',
    description:
      'Compara o faturamento mensal dos clientes do Emissor por frequência, engajamento e período.',
    technology: 'Shiny',
    navigationType: 'Redirect',
    destinationPath: 'https://inteligenciaugs.sebrae.com.br/VariacaoFaturamentoClientesEmissor/',
    redirect: true,
  },
  {
    title: 'Relatório do Agente',
    themeSlug: 'sebrae-agents-at-your-company',
    productType: 'Relatório',
    imageSource: '/report_assets/agent_report.png',
    imageAlternativeText: 'Relatório territorial de agente',
    description:
      'Apresenta atendimentos, área de foco, distribuição territorial e produtividade de agentes.',
    technology: 'Nativo',
    navigationType: 'Nativo',
    destinationPath: '/app/agent-report',
  },
  {
    title: 'Roteiros para Agentes',
    themeSlug: 'sebrae-agents-at-your-company',
    productType: 'Estudo',
    imageSource:
      'https://images.unsplash.com/photo-1594935975218-a3596da034a3?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8bWFwYSUyMGRhJTIwcm90YXxlbnwwfHwwfHx8MA%3D%3D',
    imageAlternativeText: 'Mapa de rota',
    description:
      'Recomenda roteiros de atendimento com base no histórico do agente, oportunidades e otimização de rota.',
    technology: 'Nativo',
    navigationType: 'Nativo',
    destinationPath: '/app/report/agent-advised-roadmaps',
  },
  {
    title: 'Monitoramento das Integrações dos Atendimentos',
    themeSlug: 'internal-monitoring',
    productType: 'Monitoramento',
    imageSource:
      'https://images.unsplash.com/photo-1516728043722-b394cb2f689e?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    imageAlternativeText: 'Fluxo de monitoramento',
    description:
      'Acompanha a última data de atendimento por tipo de serviço integrado e a defasagem da carga.',
    technology: 'Nativo',
    navigationType: 'Nativo',
    destinationPath: '/app/report/service-integrations-monitoring',
  },
  {
    title: 'Monitoramento do Agendamento de Disparos da Pesquisa NPS',
    themeSlug: 'internal-monitoring',
    productType: 'Monitoramento',
    imageSource: '/report_assets/nps_scheduling_monitoring.png',
    imageAlternativeText: 'Painel de monitoramento da pesquisa NPS',
    description:
      'Acompanha eventos elegíveis, disparos da pesquisa NPS, convites enviados e respostas recebidas.',
    technology: 'Power BI',
    navigationType: 'Iframe',
    destinationPath:
      '/app/external-report/monitoramento-do-agendamento-de-disparos-da-pesquisa-nps',
    isEmbedded: true,
    realLink:
      'https://app.powerbi.com/reportEmbed?reportId=2a0f20d1-69bd-435a-afdc-49c6d0df8751&autoAuth=true&ctid=97298271-1bd7-4ac5-935b-88addef636cc',
  },
  {
    title: 'Evasão de Cursos',
    themeSlug: 'others',
    productType: 'Estudo',
    imageSource:
      'https://images.unsplash.com/photo-1502465771179-51f3535da42c?q=80&w=2069&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    imageAlternativeText: 'Ambiente de estudo',
    description:
      'Analisa matrículas, início e conclusão dos cursos EAD em âmbito nacional e estadual.',
    technology: 'Nativo',
    navigationType: 'Nativo',
    destinationPath: '/app/report/course-dropout',
  },
  {
    title: 'Ciclo de Vida do Produto',
    themeSlug: 'others',
    productType: 'Estudo',
    imageSource: '/report_assets/life_cycle.png',
    imageAlternativeText: 'Ciclo de vida do produto',
    description: 'Analisa a trajetória temporal das matrículas e padrões de atividade dos cursos.',
    technology: 'Nativo',
    navigationType: 'Nativo',
    destinationPath: '/app/report/course-life-cycle',
  },
  {
    title: 'Séries Temporais',
    themeSlug: 'others',
    productType: 'Ferramenta',
    imageSource: '/report_assets/time_series.png',
    imageAlternativeText: 'Gráfico de série temporal',
    description:
      'Permite explorar indicadores ao longo do tempo com filtros territoriais e visualização em mapa.',
    technology: 'Nativo',
    navigationType: 'Nativo',
    destinationPath: '/app/time-series/',
  },
  {
    title: 'Aglomerados Subnormais',
    themeSlug: 'others',
    productType: 'Estudo',
    imageSource:
      'https://images.unsplash.com/photo-1551717256-ad2ac9ab0261?blend=000000&blend-alpha=10&blend-mode=normal&blend-w=1&crop=faces%2Cedges&h=630&mark=https%3A%2F%2Fimages.unsplash.com%2Fopengraph%2Flogo.png&mark-align=top%2Cleft&mark-pad=50&mark-w=64&w=1200&auto=format&fit=crop&q=60&ixid=M3wxMjA3fDB8MXxhbGx8fHx8fHx8fHwxNzEwNTM2Mzk0fA&ixlib=rb-4.0.3',
    imageAlternativeText: 'Vista urbana em aglomerado',
    description:
      'Apresenta análise territorial de aglomerados subnormais, estabelecimentos e atendimentos do SEBRAE.',
    technology: 'Nativo',
    navigationType: 'Nativo',
    destinationPath: '/app/report/subnormal-clusters',
  },
  {
    title: 'Outros Mapas',
    themeSlug: 'others',
    productType: 'Mapa',
    imageSource: '/report_assets/other_maps.png',
    imageAlternativeText: 'Mapa com indicadores territoriais',
    description:
      'Permite explorar geograficamente indicadores de empresas e atendimentos por diferentes recortes territoriais.',
    technology: 'Nativo',
    navigationType: 'Nativo',
    destinationPath: '/app/map/',
  },
  {
    title: 'Modelo de Potencial de Demanda para Soluções Premium',
    themeSlug: 'others',
    productType: 'Modelo analítico',
    imageSource: '/report_assets/analytical_demand_potential_model.png',
    imageAlternativeText: 'Modelo analítico de potencial de demanda',
    description:
      'Compara média histórica, execução observada e desafios futuros por produto, UF e agência.',
    technology: 'Shiny',
    navigationType: 'Redirect',
    destinationPath: 'http://inteligenciaugs.sebrae.com.br/ModeloPotencialDemanda',
    redirect: true,
  },
  {
    title: 'Modelo de Potencial de Demanda para Soluções Premium',
    themeSlug: 'others',
    productType: 'Modelo analítico',
    imageSource: '/report_assets/analytical_demand_potential_model_2.png',
    imageAlternativeText: 'Painel do modelo de potencial de demanda',
    description:
      'Estima desafios de desempenho para soluções premium por produto, modalidade, cluster e território.',
    technology: 'Power BI',
    navigationType: 'Iframe',
    destinationPath: '/app/external-report/modelo-de-potencial-de-demanda-para-solucoes-premium',
    isEmbedded: true,
    realLink:
      'https://app.powerbi.com/reportEmbed?reportId=801a833f-fb6b-4b7a-bf17-f82503cdfd74&autoAuth=true&ctid=97298271-1bd7-4ac5-935b-88addef636cc',
  },
]

export default analyticalLibraryItems
